import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

import { useAuth } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { PublicRoute } from '@/components/auth/PublicRoute';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// Pages - Lazy loaded for code splitting
const HomePage = React.lazy(() => import('@/pages/HomePage'));
const LoginPage = React.lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = React.lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('@/pages/auth/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('@/pages/auth/VerifyEmailPage'));

const DashboardPage = React.lazy(() => import('@/pages/DashboardPage'));
const PromptsPage = React.lazy(() => import('@/pages/prompts/PromptsPage'));
const PromptDetailPage = React.lazy(() => import('@/pages/prompts/PromptDetailPage'));
const CreatePromptPage = React.lazy(() => import('@/pages/prompts/CreatePromptPage'));

const WorkflowsPage = React.lazy(() => import('@/pages/workflows/WorkflowsPage'));
const WorkflowDetailPage = React.lazy(() => import('@/pages/workflows/WorkflowDetailPage'));
const WorkflowCreatePage = React.lazy(() => import('@/pages/workflows/WorkflowCreatePage'));
const WorkflowEditPage = React.lazy(() => import('@/pages/workflows/WorkflowEditPage'));

const MdFilesPage = React.lazy(() => import('@/pages/md-files/MdFilesPage'));
const MdFileDetailPage = React.lazy(() => import('@/pages/md-files/MdFileDetailPage'));
const CreateMdFilePage = React.lazy(() => import('@/pages/md-files/CreateMdFilePage'));

const ProfilePage = React.lazy(() => import('@/pages/profile/ProfilePage'));
const SettingsPage = React.lazy(() => import('@/pages/settings/SettingsPage'));
const TwoFactorPage = React.lazy(() => import('@/pages/settings/TwoFactorPage'));
const NewsCrawlerSettingsPage = React.lazy(() => import('@/pages/settings/NewsCrawlerSettingsPage'));

const NewsPage = React.lazy(() => import('@/pages/NewsPage'));
const NewsDetailPage = React.lazy(() => import('@/pages/NewsDetailPage'));
const NewsCrawlerPage = React.lazy(() => import('@/pages/admin/NewsCrawlerPage'));
const UsersPage = React.lazy(() => import('@/pages/admin/UsersPage'));

// Community Pages
const FreeBoardPage = React.lazy(() => import('@/pages/community/FreeBoardPage'));
const NoticeBoardPage = React.lazy(() => import('@/pages/community/NoticeBoardPage'));
const CreatePostPage = React.lazy(() => import('@/pages/community/CreatePostPage'));
const EditPostPage = React.lazy(() => import('@/pages/community/EditPostPage'));
const PostDetailPage = React.lazy(() => import('@/pages/community/PostDetailPage'));
const AdminSettingsPage = React.lazy(() => import('@/pages/admin/AdminSettingsPage'));
const SearchPage = React.lazy(() => import('@/pages/SearchPage'));
const NotificationsPage = React.lazy(() => import('@/pages/NotificationsPage'));
const NotFoundPage = React.lazy(() => import('@/pages/NotFoundPage'));

// Legal Pages
const PrivacyPolicyPage = React.lazy(() => import('@/pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('@/pages/legal/TermsOfServicePage'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

function App() {
  const { isLoading, isAuthenticated } = useAuth();

  // Show loading spinner while checking authentication, but only for 3 seconds max
  const [showLoader, setShowLoader] = React.useState(true);
  
  React.useEffect(() => {
    // 3초 후 강제로 로더 숨김 (새로고침 시 빠른 접근 보장)
    const timeout = setTimeout(() => {
      setShowLoader(false);
    }, 3000);
    
    // 인증 체크가 완료되면 즉시 로더 숨김
    if (!isLoading) {
      clearTimeout(timeout);
      setShowLoader(false);
    }
    
    return () => clearTimeout(timeout);
  }, [isLoading]);

  // Show loading spinner while checking authentication (최대 3초)
  // 하지만 public 페이지들은 로딩 중에도 접근 가능하도록 함
  if (isLoading && showLoader) {
    const currentPath = window.location.pathname;
    const publicPaths = ['/', '/news', '/auth/login', '/auth/register', '/auth/forgot-password'];
    
    // Public 경로가 아닌 경우에만 로더 표시
    if (!publicPaths.some(path => currentPath === path || currentPath.startsWith('/auth/'))) {
      return <PageLoader />;
    }
  }

  return (
    <div className="App">
      <AnimatePresence mode="wait">
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              <React.Suspense fallback={<PageLoader />}>
                <HomePage />
              </React.Suspense>
            }
          />
          
          {/* Auth Routes - Only accessible when not authenticated */}
          <Route
            path="/auth/login"
            element={
              <PublicRoute>
                <React.Suspense fallback={<PageLoader />}>
                  <LoginPage />
                </React.Suspense>
              </PublicRoute>
            }
          />
          <Route
            path="/auth/register"
            element={
              <PublicRoute>
                <React.Suspense fallback={<PageLoader />}>
                  <RegisterPage />
                </React.Suspense>
              </PublicRoute>
            }
          />
          <Route
            path="/auth/forgot-password"
            element={
              <PublicRoute>
                <React.Suspense fallback={<PageLoader />}>
                  <ForgotPasswordPage />
                </React.Suspense>
              </PublicRoute>
            }
          />
          <Route
            path="/auth/reset-password"
            element={
              <PublicRoute>
                <React.Suspense fallback={<PageLoader />}>
                  <ResetPasswordPage />
                </React.Suspense>
              </PublicRoute>
            }
          />
          <Route
            path="/auth/verify-email"
            element={
              <React.Suspense fallback={<PageLoader />}>
                <VerifyEmailPage />
              </React.Suspense>
            }
          />

          {/* Protected Routes - Wrapped with Layout */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <DashboardPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Prompts */}
          <Route
            path="/prompts"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <PromptsPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/prompts/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <CreatePromptPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/prompts/:id/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <CreatePromptPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/prompts/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <PromptDetailPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Workflows */}
          <Route
            path="/workflows"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <WorkflowsPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/workflows/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <WorkflowCreatePage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/workflows/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <WorkflowDetailPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/workflows/:id/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <WorkflowEditPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* MD Files */}
          <Route
            path="/md-files"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <MdFilesPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/md-files/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <CreateMdFilePage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/md-files/:id/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <CreateMdFilePage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/md-files/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <MdFileDetailPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Profile & Settings */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <ProfilePage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <SettingsPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/two-factor"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <TwoFactorPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/news-crawler"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <NewsCrawlerSettingsPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          
          {/* Notifications */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <NotificationsPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Admin */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <UsersPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <AdminSettingsPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/news-crawler"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <NewsCrawlerPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* News */}
          <Route
            path="/news"
            element={
              <Layout>
                <React.Suspense fallback={<PageLoader />}>
                  <NewsPage />
                </React.Suspense>
              </Layout>
            }
          />
          <Route
            path="/news/:id"
            element={
              <Layout>
                <React.Suspense fallback={<PageLoader />}>
                  <NewsDetailPage />
                </React.Suspense>
              </Layout>
            }
          />

          {/* Community Routes */}
          <Route
            path="/community/notice"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <NoticeBoardPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/free"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <FreeBoardPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/free/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <CreatePostPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/free/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <PostDetailPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          
          {/* Notice Board */}
          <Route
            path="/community/notice"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <NoticeBoardPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/notice/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <CreatePostPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/notice/:id/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <EditPostPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/notice/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <PostDetailPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/free/:id/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <EditPostPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Search */}
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <Layout>
                  <React.Suspense fallback={<PageLoader />}>
                    <SearchPage />
                  </React.Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Legal Pages - Public */}
          <Route
            path="/privacy"
            element={
              <Layout>
                <React.Suspense fallback={<PageLoader />}>
                  <PrivacyPolicyPage />
                </React.Suspense>
              </Layout>
            }
          />
          <Route
            path="/terms"
            element={
              <Layout>
                <React.Suspense fallback={<PageLoader />}>
                  <TermsOfServicePage />
                </React.Suspense>
              </Layout>
            }
          />

          {/* Redirects */}
          <Route
            path="/app"
            element={
              <Navigate 
                to={isAuthenticated ? "/dashboard" : "/auth/login"} 
                replace 
              />
            }
          />

          {/* 404 */}
          <Route
            path="*"
            element={
              <React.Suspense fallback={<PageLoader />}>
                <NotFoundPage />
              </React.Suspense>
            }
          />
        </Routes>
      </AnimatePresence>
    </div>
  );
}

export default App;